import { useState } from "react";
import { routes, eventResultStatusCodesMapfre } from "../utils/symbols";
import { sendClarityEvent, sendGTMEvent } from "../utils/analytics";
import { sendEventToParent } from "../utils/helpers";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const TOKEN = process.env.REACT_APP_API_TOKEN;

const buildOptions = (data) => ({
	method: "POST",
	headers: { "Content-Type": "application/json" },
	body: JSON.stringify({ ...data, token: TOKEN }),
});

const createBooking = async (data, flow) => {
	const url =
		flow === "mapfre"
			? `${API_URL}/bookings/mapfre`
			: `${API_URL}/bookings`;
	const controller = new AbortController();
	const res = await fetch(url, {
		...buildOptions(data),
		signal: controller.signal,
	});
	return res.json();
};

const updateBooking = async (locator, data) => {
	const controller = new AbortController();
	const res = await fetch(`${API_URL}/bookings/${locator}`, {
		...buildOptions(data),
		signal: controller.signal,
	});
	return { res, data: await res.json() };
};

const sendStep = async (locator, data) => {
	const controller = new AbortController();
	const res = await fetch(`${API_URL}/send/${locator}`, {
		...buildOptions(data),
		signal: controller.signal,
	});
	return res.json();
};

const finishStep = async (locator, data) => {
	const controller = new AbortController();
	const res = await fetch(`${API_URL}/finish/${locator}`, {
		...buildOptions(data),
		signal: controller.signal,
	});
	return { res, data: await res.json() };
};

const fireAnalytics = (route, data, flow) => {
	const analyticsMap = {
		[routes.bookingForm]: {
			clarityEvent: "fc-vehiculo",
			gtmEvent: "fc-vehiculo",
			messageType: "fc-vehiculo",
		},
		[routes.selectCenter]: {
			clarityEvent: "fc-taller",
			gtmEvent: "fc-taller",
			messageType: "fc-taller",
		},
		[routes.damageGlass]: {
			clarityEvent: "fc-cristal",
			gtmEvent: "fc-cristal",
			messageType: "fc-cristal",
			extraClarity: () => {
				const tag =
					data.damageGlass === "Parabrisas delantero"
						? "Reparacion (provisional)"
						: "Sustitucion";
				sendClarityEvent("fc-cristal:", data.damageGlass);
				sendClarityEvent(tag);
			},
		},
		[routes.damageType]: {
			clarityEvent: "fc-dano",
			gtmEvent: "fc-dano",
			messageType: "fc-dano",
			extraClarity: () => {
				const damageType =
					data.damageType === "small"
						? "Menor que una moneda de 2€"
						: "Mayor que una moneda de 2€";
				const tag =
					data.damageType === "small"
						? "Reparacion (provisional)"
						: "Sustitucion";
				sendClarityEvent("fc-dano:", damageType);
				sendClarityEvent(tag);
			},
		},
		[routes.damageArea]: {
			clarityEvent: "fc-area",
			gtmEvent: "fc-area",
			messageType: "fc-area",
			extraClarity: () => {
				const damageArea =
					data.damageArea === "repair" ? "Reparacion" : "Sustitucion";
				sendClarityEvent("fc-area:", damageArea);
				sendClarityEvent(damageArea);
			},
		},
		[routes.selectDateTime]: {
			clarityEvent: "fc-cita-date-time",
			gtmEvent: "fc-cita-date-time",
			messageType: "fc-cita-date-time",
		},
		[routes.bookingEnd]: {
			clarityEvent: "fc-cliente",
			gtmEvent: "fc-cliente",
			messageType: "fc-cliente",
		},
	};

	const config = analyticsMap[route];
	if (!config) return;

	sendClarityEvent(config.clarityEvent);
	if (config.extraClarity) config.extraClarity();
	sendGTMEvent(config.gtmEvent, {
		origin: data.origin,
		locator: data.locator,
	});

	if (flow !== "mapfre") {
		window.parent.postMessage(
			{ type: config.messageType, data: { origin: data.origin } },
			"*",
		);
	}
};

export const useBookingNavigation = ({
	route,
	nextPage,
	data,
	setData,
	flow,
	navigate,
	openPopUpError,
	setIsTransitioning,
	validateForm,
}) => {
	const [sending, setSending] = useState(false);

	const executeNext = async () => {
		setSending(true);

		if (route === routes.bookingForm) {
			if (!validateForm()) {
				setSending(false);
				return;
			}

			if (!data.locator) {
				try {
					const newData = await createBooking(data, flow);
					if (newData.result === "ok") {
						setData((prev) => ({
							...prev,
							locator: newData.locator,
						}));
					}
				} catch (err) {
					if (err.name !== "AbortError") console.error(err);
					sendEventToParent(
						eventResultStatusCodesMapfre.ERROR_5XX,
						data,
						flow,
					);
				}
			}
		}

		if (data.locator) {
			try {
				const { res: bookingRes, data: bookingData } =
					await updateBooking(data.locator, data);
				setData((prev) => ({
					...prev,
					...bookingData.booking,
					origin: prev.origin,
					rlid: prev.rlid,
					journey: prev.journey,
					centerCode: prev.centerCode,
					date: prev.date ?? bookingData.booking.date,
					startTime: prev.startTime ?? bookingData.booking.startTime,
					endTime: prev.endTime ?? bookingData.booking.endTime,
					plate: prev.plate ?? bookingData.booking.plate,
					plateNumber: prev.plateNumber ?? bookingData.booking.plate,
					damageArea: prev.damageArea ?? bookingData.booking.type,
				}));
				if (bookingRes.status === 400) {
					openPopUpError();
					sendEventToParent(
						eventResultStatusCodesMapfre.ERROR_5XX,
						data,
						flow,
					);
					setSending(false);
					return;
				}
			} catch (err) {
				if (err.name !== "AbortError") console.error(err);
				sendEventToParent(
					eventResultStatusCodesMapfre.ERROR_5XX,
					data,
					flow,
				);
			}
		}

		if (nextPage === routes.selectDateTime) {
			try {
				const sendData = await sendStep(data.locator, data);
				if (
					sendData.remote?.status === 409 ||
					sendData.remote?.message?.include(
						"Hay uno o varios servicios vigentes para la matrícula",
					)
				) {
					setData((prev) => ({ ...prev, bookingExists: true }));
					setIsTransitioning(false);
					setSending(false);
					navigate(routes.errorPage);
					return;
				}
				if (sendData.result === "nok") {
					setIsTransitioning(false);
					setSending(false);
					navigate(routes.errorPage);
					sendEventToParent(
						eventResultStatusCodesMapfre.ERROR_5XX,
						data,
						flow,
					);
					return;
				}
			} catch (err) {
				if (err.name !== "AbortError") console.error(err);
				sendEventToParent(
					eventResultStatusCodesMapfre.ERROR_5XX,
					data,
					flow,
				);
			}
		}

		const isMapfreFinish =
			route === routes.selectDateTime &&
			flow === "mapfre" &&
			data.date &&
			data.time &&
			data.locator;
		const isWebFinish = route === routes.bookingEnd && flow !== "mapfre";

		if (isMapfreFinish || isWebFinish) {
			if (isWebFinish && !validateForm()) {
				setSending(false);
				return;
			}

			try {
				const { res: finishRes, data: responseData } = await finishStep(
					data.locator,
					data,
				);

				if (route === routes.bookingEnd && finishRes.status === 400) {
					if (
						responseData.message ===
						"Ya existe una reserva finalizada"
					) {
						setData((prev) => ({ ...prev, bookingExists: true }));
					}
					setIsTransitioning(false);
					setSending(false);
					navigate(routes.errorPage);
					return;
				}

				if (flow === "mapfre" && finishRes.status === 400) {
					sendEventToParent(
						eventResultStatusCodesMapfre.ERROR_4XX,
						data,
						flow,
					);
				} else if (flow === "mapfre" && responseData.result === "ok") {
					sendEventToParent(
						eventResultStatusCodesMapfre.SUCCESS,
						data,
						flow,
					);
				}

				if (
					responseData.result === "ok" &&
					responseData.booking?.code
				) {
					setData((prev) => ({
						...prev,
						code: responseData.booking.code,
					}));
				}

				if (responseData.result === "nok") {
					setIsTransitioning(false);
					setSending(false);
					navigate(routes.errorPage);
					sendEventToParent(
						eventResultStatusCodesMapfre.ERROR_5XX,
						data,
						flow,
					);
					return;
				}
			} catch (err) {
				if (err.name !== "AbortError") console.error(err);
				sendEventToParent(
					eventResultStatusCodesMapfre.ERROR_5XX,
					data,
					flow,
				);
			}
		}

		fireAnalytics(route, data, flow);

		setIsTransitioning(false);
		setSending(false);

		if (
			data.journey === "B" &&
			data.date &&
			data.startTime &&
			data.centerId &&
			!data.centerFromParam &&
			nextPage === routes.selectDateTime
		) {
			navigate(routes.bookingEnd);
		} else {
			navigate(nextPage);
		}
	};

	return { executeNext, sending };
};
